import React, { createElement, useEffect, useMemo, useState } from "react";
import * as Core from "@blueprintjs/core";
import classnames from "classnames";
import { uniqueId } from "lodash-es";
import { splitDataAttrs } from "~/utils";
import { useColorScheme } from "./theme";
import { type ToneProps, toneClass } from "./tone";

export interface TooltipProps<TProps extends React.HTMLProps<HTMLElement> = React.HTMLProps<HTMLElement>>
  extends ToneProps, Omit<React.PropsWithChildren<Core.TooltipProps<TProps>>, "position" | "intent"> {
  /** @default 400 */
  maxWidth?: number | string | null;
}

export function Tooltip<TProps extends React.HTMLProps<HTMLElement> = React.HTMLProps<HTMLElement>>(props: TooltipProps<TProps>) {
  const {
    children,
    content,
    className,
    disabled: controlledDisabled,
    fill,
    isOpen: controlledIsOpen,
    hoverOpenDelay = 300,
    maxWidth = 400,
    inheritDarkTheme = true,
    targetTagName = "span",
    openOnTargetFocus = false,
    onInteraction,
    popoverClassName: controlledPopoverClassName,
    placement = "top",
    renderTarget: controlledRenderTarget,
    targetProps: controlledTargetProps,
    tone,
    ...restProps
  } = props;

  const { colorScheme } = useColorScheme();

  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [forceDisabled, setForceDisabled] = useState(false);
  const targetClassName = classnames("tooltip", className, Core.Classes.POPOVER_TARGET, {
    [Core.Classes.FILL]: fill,
  });

  const popoverClassName = classnames(controlledPopoverClassName, toneClass(tone), {
    [Core.Classes.DARK]: inheritDarkTheme && colorScheme.dark,
  });

  const tooltipId = useMemo(() => uniqueId("tooltip-"), []);

  const hasNoContent = typeof content === "string" && content.trim().length === 0;
  const disabled = controlledDisabled || forceDisabled || hasNoContent;

  useEffect(() => {
    if (forceDisabled) {
      document.addEventListener("mouseover", handleMouseOver);
      window.addEventListener("focus", handleWindowFocus);
    } else {
      window.addEventListener("blur", handleWindowBlur);
    }

    return () => {
      document.removeEventListener("mouseover", handleMouseOver);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [forceDisabled]);

  // Target can only be one element, so use fragment to force all given elements to render.
  // This will render a div or span within the target
  const target = Array.isArray(children) ? <>{children}</> : children;

  const { props: tooltipProps, dataAttrs } = splitDataAttrs(restProps);

  const modifiers = useMemo<Core.PopperModifierOverrides>(
    () => ({
      arrow: {
        ...tooltipProps.modifiers?.arrow,
        options: {
          padding: 6,
          ...tooltipProps.modifiers?.arrow?.options,
        },
      },
      flip: {
        ...tooltipProps.modifiers?.flip,
        options: {
          fallbackPlacements: [oppositePlacement(placement), "auto"],
        },
      },
      preventOverflow: {
        ...tooltipProps.modifiers?.preventOverflow,
        options: {
          padding: 15,
          ...tooltipProps.modifiers?.preventOverflow?.options,
        },
      },
    }),
    [tooltipProps.modifiers]
  );

  return (
    <Core.Tooltip
      {...tooltipProps}
      content={content}
      disabled={disabled}
      hoverOpenDelay={hoverOpenDelay}
      inheritDarkTheme={inheritDarkTheme}
      isOpen={controlledIsOpen ?? uncontrolledIsOpen}
      modifiers={modifiers}
      popoverRef={handlePopoverRef}
      openOnTargetFocus={openOnTargetFocus}
      placement={placement}
      popoverClassName={popoverClassName}
      renderTarget={renderTarget}
      onInteraction={handleInteraction}
    />
  );

  function handlePopoverRef(el: HTMLElement | null) {
    if (el && !el.id) {
      el.style.maxWidth = maxWidth ? (typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth) : "";
      el.setAttribute("id", tooltipId);
      el.setAttribute("data-random-id", "true");
      el.setAttribute("role", "tooltip");
    }
  }

  function renderTarget(...args: Parameters<NonNullable<Core.TooltipProps["renderTarget"]>>) {
    const targetProps = {
      ...dataAttrs,
      ...controlledTargetProps,
      ...args[0],
      className: targetClassName,
      "aria-describedby": tooltipId,
    };

    if (controlledRenderTarget) {
      return controlledRenderTarget(targetProps);
    }

    const elementProps = {
      ...targetProps,
    };

    delete (elementProps as any).isOpen;

    return createElement(targetTagName, elementProps, target);
  }

  function handleInteraction(nextOpenState: boolean, e?: React.SyntheticEvent<HTMLElement>) {
    // If disabled, ignore attempt to open.  This helps if disabled was set to true during the hover open delay timer.
    if (disabled && uncontrolledIsOpen) {
      setUncontrolledIsOpen(false);
      onInteraction?.(false, e);
    } else {
      setUncontrolledIsOpen(nextOpenState);
      onInteraction?.(nextOpenState, e);
    }
  }

  function handleMouseOver() {
    setForceDisabled(false);
  }

  function handleWindowFocus() {
    setForceDisabled(false);
  }

  function handleWindowBlur() {
    setForceDisabled(true);
  }
}

function oppositePlacement(placement: Core.Placement): Core.Placement {
  switch (placement) {
    case "auto":
      return "auto";
    case "auto-end":
      return "auto-start";
    case "auto-start":
      return "auto-end";
    case "bottom":
      return "top";
    case "bottom-start":
      return "top-start";
    case "bottom-end":
      return "top-end";
    case "top":
      return "bottom";
    case "top-start":
      return "bottom-start";
    case "top-end":
      return "bottom-end";
    case "left":
      return "left";
    case "left-start":
      return "left-start";
    case "left-end":
      return "left-end";
    case "right":
      return "right";
    case "right-start":
      return "right-start";
    case "right-end":
      return "right-end";
  }
}
